/**
 * Password reset page for handling Supabase recovery links
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useToast } from '../context/ToastContext';
import { Button, Input } from '../components/common';
import './Login.css';

/**
 * Password reset page that handles recovery tokens from Supabase email links
 * @returns {React.ReactElement} Reset password page element
 */
export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [error, setError] = useState(null);

  const { addToast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    // Listen for auth state changes to detect recovery session
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'PASSWORD_RECOVERY') {
          setSessionReady(true);
          setError(null);
        } else if (event === 'SIGNED_IN' && session) {
          // User might already be in recovery mode from the URL token
          setSessionReady(true);
          setError(null);
        }
      }
    );

    // Check if we already have a session (token was processed)
    supabase.auth.getSession().then(({ data: { session }, error: sessionError }) => {
      if (sessionError) {
        setError('Invalid or expired reset link. Please request a new one.');
      } else if (session) {
        setSessionReady(true);
      } else {
        // No session and no error - link might be invalid or expired
        setError('Invalid or expired reset link. Please request a new one.');
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      addToast({ type: 'error', message: 'Passwords do not match' });
      return;
    }

    if (password.length < 6) {
      addToast({ type: 'error', message: 'Password must be at least 6 characters' });
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) throw updateError;

      addToast({ type: 'success', message: 'Password updated successfully' });

      // Sign out and redirect to login
      await supabase.auth.signOut();
      navigate('/login', { replace: true });
    } catch (err) {
      addToast({
        type: 'error',
        message: err.message || 'Failed to update password'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-card__header">
          <img src="/favicon.svg" alt="Logo" className="login-card__logo" />
          <h1 className="login-card__title">My Whiskey Collection</h1>
          <p className="login-card__subtitle">Set your new password</p>
        </div>

        {error ? (
          <div className="login-card__form">
            <p style={{ color: 'var(--color-error)', textAlign: 'center', marginBottom: 'var(--spacing-md)' }}>
              {error}
            </p>
            <Button onClick={() => navigate('/login')} fullWidth>
              Back to Login
            </Button>
          </div>
        ) : !sessionReady ? (
          <div className="login-card__form">
            <p style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>
              Verifying reset link...
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="login-card__form">
            <Input
              label="New Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter new password"
              required
              autoComplete="new-password"
            />
            <Input
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              autoComplete="new-password"
            />
            <Button type="submit" fullWidth loading={loading}>
              Update Password
            </Button>
            <button
              type="button"
              className="login-card__link"
              onClick={() => navigate('/login')}
            >
              Back to sign in
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
